import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BootstrapService } from 'app/service/bootstrap.service';
import { UserService } from 'app/service/user.service';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { normalizeAttempts, allAssessmentsDone } from '../../ui/assessment-summary';
declare var bootstrap: any;

@Component({
  standalone: false,
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  managerAssessmentOpen = false;
  get showQuestionaryMenu(): boolean {
    return this.isManager ? this.managerAssessmentOpen : this.isEmployee || this.isApplicant || this.isTrainee;
  }
  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }
  refreshManagerAssessment(): void {
    if (!this.isManager || !this.user?.uuid) return;
    this.userService.getAttemptQuestionary(this.user.uuid).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (res: unknown) => { const attempts = normalizeAttempts(res); this.managerAssessmentOpen = attempts.length > 0 && !allAssessmentsDone(attempts); },
      error: () => this.managerAssessmentOpen = true
    });
  }

  searchText: any;
  toggle = true;
  status = 'Enable';
  isAdmin = false;
  isManager = false;
  isEmployee = false;
  isApplicant = false;
  isTrainee = false;
  selectedLanguage: any = 'en';
  currentTheme: any = 'dark';
  newValue: string = 'Initial Value';
  roleBased: any;
  questionary: any;
  showReportMenu: boolean = false;
  logoSrc: any;
  reminderTime: number = 24;
  profileImagePreview: string | ArrayBuffer | null = null;
  user: any;
  supportedLanguages: { flag: string, code: string, name: string }[] = [
    { flag: '', code: 'en', name: 'EN' },
    { flag: '', code: 'de', name: 'DE' },
    { flag: '', code: 'pt', name: 'PT' },
    { flag: '', code: 'fr', name: 'FR' }
  ];
  constructor(
    private translate: TranslateService,
    public router: Router,
    private userService: UserService,
    private renderer: Renderer2,
    private el: ElementRef,
    private bootstrapService: BootstrapService,
    private toastr: ToastrService,
    public cookieService: CookieService,
  ) {
    translate.setDefaultLang('en');
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
      translate.use(savedLanguage);
    } else {
      translate.use('en');
    }
    this.renderer.addClass(
      this.el.nativeElement.ownerDocument.body,
      'dark-theme-selector'
    );
  }

  ngOnInit() {
    this.getUserLanguage();
    this.getRole();
    this.getUserQuestionaries();
    this.getCompanyLogo();
    this.getMe(); 
    this.userService.companyLogo$.pipe(takeUntil(this.destroyed$)).subscribe(url => {
      if (url) this.logoSrc = url;
    });
    this.userService.questionnaireStatus$.pipe(takeUntil(this.destroyed$)).subscribe(status => {
      if (this.isManager && status) this.refreshManagerAssessment();
      if (status === 'DONE') {
        this.questionary = 'DONE';
        if (this.roleBased === 'APPLICANT') {
          this.showReportMenu = this.isApplicant && status === 'DONE';
        } else if (this.roleBased === 'EMPLOYEE') {
          this.showReportMenu = this.isEmployee && status === 'DONE';
        } else if (this.roleBased === 'TRAINEE') {
          this.showReportMenu = this.isTrainee && status === 'DONE';
        }
      }
    });
  }
  getMe() {
    this.userService.getMe().subscribe((res: any) => {
      this.user = res;
      this.profileImagePreview = res.profile_picture || null;
      this.refreshManagerAssessment();
    });
  }
  
  getCompanyLogo() {
    const companyId = localStorage.getItem('companyId');
    if (companyId) {
      this.userService.getOrganization(companyId).subscribe((res: any) => {
        this.logoSrc = res.company_logo;
      });
    }
  }


  // Auf der Profilseite ist der Avatar hervorgehoben (das Profil steht im Avatar-Menü, nicht in der Navigationsleiste)
  get onProfile(): boolean {
    return this.router.url.startsWith('/profile');
  }

  companyDetail() {
    this.router.navigate(['/company-detail']);
  }
  getRole() {
    this.roleBased = this.cookieService.get('userRole');
    if (this.roleBased == 'MANAGER') {
      this.isManager = true;
      this.isAdmin = false;
      this.isEmployee = false;
      this.isApplicant = false;
      this.isTrainee = false;
    } else if (this.roleBased == 'ADMIN') {
      this.isAdmin = true;
      this.isManager = false;
      this.isEmployee = false;
      this.isApplicant = false;
      this.isTrainee = false;
    } else if (this.roleBased == 'EMPLOYEE') {
      this.isEmployee = true;
      this.isManager = false;
      this.isAdmin = false;
      this.isApplicant = false;
      this.isTrainee = false;
    } else if (this.roleBased == 'APPLICANT') {
      this.isEmployee = false;
      this.isManager = false;
      this.isAdmin = false;
      this.isApplicant = true;
      this.isTrainee = false;
    } else if (this.roleBased == 'TRAINEE') {
      this.isEmployee = false;
      this.isManager = false;
      this.isAdmin = false;
      this.isApplicant = false;
      this.isTrainee = true;
    }
    const storedLanguage = localStorage.getItem('selectedLanguage');
    if (storedLanguage) {
      this.selectedLanguage = storedLanguage;
    } else {
      this.selectedLanguage = 'en';
      localStorage.setItem('selectedLanguage', 'en');
    }
    this.translate.use(this.selectedLanguage);
  }


  changeLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
    this.userService.loadLang.next(lang);
    let lang1 = localStorage.getItem('selectedLanguage');
    let userItem = localStorage.getItem('newDep');
    let candidateItem = localStorage.getItem('defultCandidate')
    let uId = localStorage.getItem('userId')
    let OrgId = localStorage.getItem('organizationId')
    let newUser = localStorage.getItem('selectedReport')
    let attempts = localStorage.getItem('selectedAttempt');
    let useruid = localStorage.getItem('useruuId')
    if (this.router.url === '/questionary') {
      this.userService.getUserQuestionaries(uId, attempts).subscribe((res) => {
        this.userService.updateValues(res, lang1);
      })
    } else if (this.router.url === '/report') {
      this.userService.getCorridorReport(OrgId, useruid, userItem).subscribe((res) => {
        this.userService.updateCorridor(res, lang1);
      })
      this.userService.getCorridorCandidate(OrgId, candidateItem).subscribe((res) => {
        this.userService.updateCandidate(res, lang1);

      })
      this.userService.getCorridorIndividualEmployee(OrgId, useruid).subscribe((res) => {
        this.userService.updateEmployees(res, lang1);

      })
      location.reload()
    } else if (this.router.url === '/report/user-detail') {
      this.userService.getReportDetails(newUser).subscribe((res) => {
        this.userService.updateGenerate(res, lang1);
        location.reload();
      })
    }
  }

  getUserLanguage() {
    this.userService.getMe().subscribe((res: any) => {
      if (res.language) {
        const backendLang = res.language.toLowerCase();
        const supportedLang = this.supportedLanguages.find(lang => lang.code === backendLang);

        if (supportedLang) {
          this.selectedLanguage = backendLang; 
        } else {
          this.selectedLanguage = 'en'; 
        }

        this.translate.use(this.selectedLanguage);
        localStorage.setItem('selectedLanguage', this.selectedLanguage);
      }
    });
  }

  getLanguageName(code: string): string {
    const language = this.supportedLanguages.find(lang => lang.code === code);
    return language ? language.name : 'Language';
  }

  getLanguageFlag(code: string): string {
    const language = this.supportedLanguages.find(lang => lang.code === code);
    return language ? language.flag : '';
  }

  sendReminder(time: number) {
    this.toastr.info(this.translate.instant('uiPack.navigation.reminderUnavailable'));
    this.closeModal();
  }

  closeModal() {
    const modal = document.getElementById('remainderModal');
    if (modal) {
      const modalInstance = bootstrap.Modal.getInstance(modal);
      if (modalInstance) {
        modalInstance.hide();  // Close the modal
      }
    }
  }
  logOut() {
    this.userService.logout();
  }
  confirmDeleteDialog(id: any) {
    this.userService.deleteUser(id).subscribe((res: any) => {
      this.toastr.success('User deleted successfully', 'Success', { timeOut: 2000 });
    });
  }


  getUserQuestionaries() {
    let uId = localStorage.getItem('candidateId');
    let attempt = localStorage.getItem('selectedAttempt') || '1';

    if (this.roleBased === 'APPLICANT' || this.roleBased === 'EMPLOYEE' || this.roleBased === 'TRAINEE') {
      this.userService.getUserQuestionaries(uId, attempt).subscribe((res: any) => {
        if (res.length > 0) {
          this.questionary = res[0].status;

          if (this.roleBased === 'APPLICANT') {
            this.showReportMenu = this.isApplicant && this.questionary === 'DONE';
          } else if (this.roleBased === 'EMPLOYEE') {
            this.showReportMenu = this.isEmployee && this.questionary === 'DONE';
          } else if (this.roleBased === 'TRAINEE') {
            this.showReportMenu = this.isTrainee && this.questionary === 'DONE';
          } else {
            this.showReportMenu = false;
          }

          // Update the status in the service
          if (this.questionary === 'DONE') {
            this.userService.updateQuestionnaireStatus('DONE');
          }
        } else {
          this.showReportMenu = false;
        }
      });
    }
  }

}
